import React, { useRef, useEffect, useState } from 'react';
import { ChevronUp, Phone, ExternalLink } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';
import analyticsService from '../services/analyticsService';
import { HelpCard as IHelpCard } from '../types/helpCard.types';
import { useTranslate } from '../lib/i18n';
import BackgroundIcon from './common/BackgroundIcon';

interface Props {
  data: IHelpCard;
  isExpanded: boolean;
  onToggle: (cardId: string) => void;
  iconIndex?: number;
}

const HelpCard = ({ data, isExpanded, onToggle, iconIndex = 0 }: Props) => {
  const { title, description, phone, linkUrl, linkText, additionalInfo } = data;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxContentHeight, setMaxContentHeight] = useState(0);
  const t = useTranslate();

  const translateField = (value?: string | null) => {
    if (!value) return '';
    const translated = t(value);
    return translated === `[${value}]` ? null : translated;
  };

  const titleText = translateField(title);
  const descriptionText = translateField(description);
  const additionalInfoText = translateField(additionalInfo);
  const phoneText = translateField(phone);
  const linkTextTranslated = translateField(linkText);
  const linkUrlTranslated = translateField(linkUrl);
  const hasAdditionalInfo = Boolean(additionalInfoText);

  useEffect(() => {
    if (contentRef.current) {
      // Measure content height using same method as InformationCard
      const tempDiv = document.createElement('div');
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.position = 'absolute';
      tempDiv.style.width = contentRef.current.offsetWidth + 'px';
      tempDiv.className = contentRef.current.className;

      // Create content element with all the contact information
      const contentElement = document.createElement('div');
      contentElement.innerHTML = ReactDOMServer.renderToString(
        <div className="relative overflow-hidden px-4 pt-4">
          <div className="min-h-[80px]">
            <p className="text-muted-foreground mb-4 text-sm">{descriptionText}</p>
            {hasAdditionalInfo && (
              <p className="text-muted-foreground mb-4 text-xs">{additionalInfoText}</p>
            )}
            {phoneText && (
              <a href={`tel:${phoneText}`} className="mb-3 flex items-center">
                <span className="mr-2 text-primary underline">{phoneText}</span>
                <Phone className="h-4 w-4 text-primary" />
              </a>
            )}
            {linkUrlTranslated && (
              <a
                href={linkUrlTranslated}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-3 flex items-center"
              >
                <span className="mr-2 text-primary underline">
                  {linkTextTranslated ? linkTextTranslated : linkUrlTranslated}
                </span>
                <ExternalLink className="h-4 w-4 text-primary" />
              </a>
            )}
          </div>
        </div>
      );

      tempDiv.appendChild(contentElement);
      document.body.appendChild(tempDiv);
      const height = tempDiv.scrollHeight;
      document.body.removeChild(tempDiv);
      setMaxContentHeight(height + 30); // Same padding as InformationCard
    }
  }, [data, descriptionText, additionalInfoText, phoneText, linkUrlTranslated, linkTextTranslated]);

  const handleClick = () => {
    if (!isExpanded) {
      analyticsService.trackEvent({
        eventName: 'helpCardOpened',
        properties: { cardTitle: title },
      });
    }
    onToggle(data.id);
  };

  return (
    <div className="w-full overflow-hidden rounded border bg-card text-card-foreground">
      <div
        className="relative flex cursor-pointer items-center bg-muted px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center justify-center">
          <span className="text-center font-button">{titleText}</span>
        </div>
        <div className="absolute right-2">
          <ChevronUp
            className={`transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            style={{ transitionDuration: '500ms' }}
          />
        </div>
      </div>
      <div
        ref={contentRef}
        className="relative overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          height: isExpanded ? `${maxContentHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div className="relative border-t">
          <div className="relative overflow-hidden px-4 pt-4">
            <div className="min-h-[80px]">
              <div className="relative whitespace-normal break-words text-sm z-3">
                <p className="text-muted-foreground mb-4">{descriptionText}</p>
                {phoneText && (
                  <a href={`tel:${phoneText}`} className="mb-3 flex items-center">
                    <span className="mr-2 text-primary underline">{phoneText}</span>
                    <Phone className="h-4 w-4 text-primary" />
                  </a>
                )}
                {linkUrlTranslated && (
                  <a
                    href={
                      linkUrlTranslated.startsWith('http')
                        ? linkUrlTranslated
                        : `http://${linkUrlTranslated}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-3 block w-fit"
                  >
                    <span className="inline text-primary underline">
                      {linkTextTranslated ? linkTextTranslated : linkUrlTranslated}
                    </span>
                    <ExternalLink className="ml-1 inline-block h-4 w-4 align-text-bottom text-primary" />
                  </a>
                )}
                {hasAdditionalInfo && (
                  <p className="text-muted-foreground mb-4">{additionalInfoText}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <BackgroundIcon iconIndex={iconIndex} />
      </div>
    </div>
  );
};

export default HelpCard;
